import type { FormEvent, ReactNode } from 'react';

interface FormProps {
  onSubmit: () => void;
  children: ReactNode;
  /** Optional test id (default `form`). */
  testId?: string;
  className?: string;
  id?: string;
}

/**
 * Thin <form> wrapper that prevents default and forwards the submit event.
 * Deliberately minimal — we don't need react-hook-form for MVP. Use with
 * the `useForm` hook (see `admin/src/hooks/useForm.ts`) for controlled state.
 */
export default function Form({
  onSubmit,
  children,
  testId,
  className,
  id,
}: FormProps) {
  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    onSubmit();
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid={testId ?? 'form'}
      className={className}
      id={id}
      noValidate
    >
      {children}
    </form>
  );
}

interface FieldProps {
  label: string;
  htmlFor?: string;
  error?: string | null;
  hint?: ReactNode;
  required?: boolean;
  children: ReactNode;
  testId?: string;
}

/**
 * Label + input + inline error layout helper. Pass the actual <input>,
 * <select>, or <textarea> as children. Attach `id` matching `htmlFor`
 * on the child for proper label association.
 */
export function Field({
  label,
  htmlFor,
  error,
  hint,
  required,
  children,
  testId,
}: FieldProps) {
  return (
    <div
      className="space-y-1"
      data-testid={testId ?? (htmlFor ? `field-${htmlFor}` : 'field')}
    >
      <label
        htmlFor={htmlFor}
        className="block text-sm font-medium text-slate-700"
      >
        {label}
        {required && (
          <span className="text-red-600 ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {hint && !error && (
        <p className="text-xs text-slate-500">{hint}</p>
      )}
      {error && (
        <p
          className="text-xs text-red-700"
          role="alert"
          data-testid={htmlFor ? `field-${htmlFor}-error` : 'field-error'}
        >
          {error}
        </p>
      )}
    </div>
  );
}
